import { Component, inject, input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { BehaviorSubject, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap, take, takeUntil } from "rxjs/operators";
import { HistoryLogSidePanelService } from "../services/history-log-side-panel.service";
import {
	MilestoneEventHistoryLogData,
	TransactionHistoryLogData,
	HistoryLogType,
	MilestoneHistoryLogType,
	MilestoneHistoryLog
} from "../models/history-log-interface";
import { TransactionReport } from "@shared/models/payouts.types";
import { CalendarErrorRecordItem } from "@modules/global-dashboard/models/global-dashboard-interface";
import { GlobalDashboardEventsService } from "@modules/global-dashboard/services/global-dashboard-events.service";

@Component({
	selector: "app-history-log-side-panel",
	templateUrl: "./history-log-side-panel.component.html",
	styleUrls: ["./history-log-side-panel.component.scss"]
})
export class HistoryLogSidePanelComponent implements OnInit, OnDestroy {
	historyType = input<string>("");

	panelTitle = "";
	exit = false;
	searchForm!: FormGroup;
	searchText = "";
	activeMilestoneHistoryFilterIndex = 0;

	milestoneIdentifierData!: MilestoneEventHistoryLogData;
	milestoneHistoryRecords!: MilestoneHistoryLog[];
	milestoneHistoryRecordsFiltered: MilestoneHistoryLog[] = [];

	transactionIdentifierData!: TransactionHistoryLogData;
	statusHistoryRecordsTransactions!: TransactionReport[];
	statusHistoryRecordsFilterCopyTransactions: TransactionReport[] = [];

	recordsFetched = false;

	HistoryLogType = HistoryLogType;

	private destroy$ = new Subject<void>();

	private formBuilder = inject(FormBuilder);
	private historyLogService = inject(HistoryLogSidePanelService);
	private globalDashboardEventsService = inject(GlobalDashboardEventsService);

	ngOnInit(): void {
		this.recordsFetched = false;
		this.setupSearchForm();
		this.subscribeToHistoryLogSubject();

		if (this.historyType() === HistoryLogType.MILESTONE) {
			this.getMilestoneHistoryRecords();
			this.panelTitle = "History Log";
		}
		if (this.historyType() === HistoryLogType.TRANSACTION) {
			this.getTransactionHistoryRecords();
			this.panelTitle = "Transaction History Log";
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	setupSearchForm(): void {
		this.searchForm = this.formBuilder.group({
			searchControl: [""]
		});

		this.searchForm
			.get("searchControl")
			?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
			.subscribe((newValue: string) => {
				this.searchText = newValue;

				this.applyFilters();
			});
	}

	applyFilters(): void {
		if (this.historyType() === HistoryLogType.MILESTONE) {
			this.milestoneHistoryRecordsFiltered = this.milestoneHistoryRecords
				.filter(record => this.filterMilestoneHistoryRecordsByType(record))
				.filter(record => this.filterMilestoneHistoryRecordsBySearch(record));
		} else if (this.historyType() === HistoryLogType.TRANSACTION) {
			this.searchTransactionHistoryRecords();
		}
	}

	filterMilestoneHistoryRecordsByType(record: MilestoneHistoryLog): boolean {
		switch (this.activeMilestoneHistoryFilterIndex) {
			case 1:
				return record.historyLogType === MilestoneHistoryLogType.STATUS;
			case 2:
				return record.historyLogType === MilestoneHistoryLogType.CALENDAR_ERROR;
			default:
				return true;
		}
	}

	filterMilestoneHistoryRecordsBySearch(record: MilestoneHistoryLog): boolean {
		if (!this.searchText) return true;

		const query = this.searchText.toLowerCase();
		const titleMatch = !this.isCalendarError(record)
			? record.title?.some(segment => segment.text.toLowerCase().includes(query)) ?? false
			: record.title.toLowerCase().includes(query);

		const subtitleMatch = record.subtitle ? record.subtitle.toLowerCase().includes(query) : false;

		return titleMatch || subtitleMatch;
	}

	searchTransactionHistoryRecords() {
		const searchText = this.searchText.toLowerCase();
		this.statusHistoryRecordsFilterCopyTransactions = this.statusHistoryRecordsTransactions.filter(record =>
			this.buildRecordText(record).toLowerCase().includes(searchText)
		);
	}

	buildRecordText(record: TransactionReport): string {
		const employeeName = [record.employeeFirstName, record.employeeLastName].filter(Boolean).join(" ");

		const parts = [
			"has changed the transaction state",
			employeeName && `of ${employeeName}`,
			record.transactionId && `with the id ${record.transactionId}`,
			`to ${record.status.name}`
		];

		return parts.filter(Boolean).join(" ");
	}

	subscribeToHistoryLogSubject(): void {
		this.getSubject()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: res => {
					if (!res) {
						this.exit = true;
					} else {
						if (this.historyType() === HistoryLogType.MILESTONE) {
							this.milestoneIdentifierData = res as MilestoneEventHistoryLogData;
						}
						if (this.historyType() === HistoryLogType.TRANSACTION) {
							this.transactionIdentifierData = res as TransactionHistoryLogData;
						}
					}
				}
			});
	}

	getMilestoneHistoryRecords(): void {
		this.historyLogService
			.getMilestone(this.milestoneIdentifierData.milestoneId)
			.pipe(
				take(1),
				switchMap(res => this.historyLogService.getAllHistories(res))
			)
			.subscribe({
				next: records => {
					this.milestoneHistoryRecords = this.milestoneHistoryLogItems(records);
					this.milestoneHistoryRecordsFiltered = this.milestoneHistoryLogItems(records);

					this.recordsFetched = true;
				}
			});
	}

	/**
	 * @param records: milestone history log items
	 * @returns milestone history log items that include calendar error records if any exist
	 */
	milestoneHistoryLogItems(records: MilestoneHistoryLog[]): MilestoneHistoryLog[] {
		const calendarErrors = this.historyLogService
			.filteredMilestonesHistoryLogCalendarItems()
			.filter(errList => errList.payCycleId === this.milestoneIdentifierData.payCycleId);

		return calendarErrors.length > 0
			? [...records, ...calendarErrors].sort(
					(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
			  )
			: records;
	}

	/**
	 * Redirect user to Calendar page
	 * @param payGroupId
	 * @param legalEntityId
	 * @param customerName
	 * @param customerId
	 */
	goToCalendar(payGroupId: string, legalEntityId: string, customerName: string, customerId: string): void {
		this.resetSubjects();
		this.globalDashboardEventsService.navigateToCalendarPage(payGroupId, legalEntityId, customerName, customerId);
	}

	/**
	 * Checks if the given history log record is a calendar error
	 * @param record: history log items
	 * @returns true if it's calendar error item
	 */
	isCalendarError(record: MilestoneHistoryLog): record is CalendarErrorRecordItem {
		return record.historyLogType === MilestoneHistoryLogType.CALENDAR_ERROR;
	}

	getTransactionHistoryRecords(): void {
		this.historyLogService
			.getTransactionHistory(
				this.transactionIdentifierData.payoutId,
				this.transactionIdentifierData.paymethodId,
				"2000-01-01",
				"2050-12-31"
			)
			.pipe(take(1))
			.subscribe({
				next: res => {
					const transactions = res.items;
					this.statusHistoryRecordsTransactions = transactions;

					// Create a new array to store the reprocessed transactions
					const reprocessedTransactions = [];

					for (let i = 1; i < this.statusHistoryRecordsTransactions.length; i++) {
						if (
							this.statusHistoryRecordsTransactions[i].transactionId !==
							this.statusHistoryRecordsTransactions[i - 1].transactionId
						) {
							const reprocessedTransaction: TransactionReport = {
								...this.statusHistoryRecordsTransactions[i],
								status: { name: "REPROCESSED", message: "" }
							};
							reprocessedTransactions.push(reprocessedTransaction);
						}
					}

					// Concatenate the original transactions with the reprocessed transactions
					this.statusHistoryRecordsTransactions = [
						...this.statusHistoryRecordsTransactions,
						...reprocessedTransactions
					];

					this.statusHistoryRecordsTransactions = this.statusHistoryRecordsTransactions!.sort(
						(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
					);

					this.statusHistoryRecordsFilterCopyTransactions = this.statusHistoryRecordsTransactions;
					this.recordsFetched = true;
				}
			});
	}

	getSubject(): BehaviorSubject<MilestoneEventHistoryLogData | TransactionHistoryLogData> {
		let subject: BehaviorSubject<MilestoneEventHistoryLogData | TransactionHistoryLogData>;

		if (this.historyType() === HistoryLogType.MILESTONE) {
			subject = this.historyLogService?.milestoneEventHistoryLogSubject as BehaviorSubject<
				MilestoneEventHistoryLogData | TransactionHistoryLogData
			>;
		}

		if (this.historyType() === HistoryLogType.TRANSACTION) {
			subject = this.historyLogService?.transactionHistoryLogSubject as BehaviorSubject<
				MilestoneEventHistoryLogData | TransactionHistoryLogData
			>;
		}

		return subject!;
	}

	resetSubjects(): void {
		this.historyLogService.resetElementData();
	}

	isArray(value: string | string[]): boolean {
		return Array.isArray(value);
	}

	getArrayValue(value: string | string[]): string[] {
		if (this.isArray(value)) {
			return Array.isArray(value) ? value : [value];
		}
		return [];
	}

	isEmptyObject(object: Record<string, unknown> | undefined): boolean {
		return !object || Object.keys(object).length === 0;
	}

	isValidName(value: string | null | undefined): boolean {
		return !!value && value.toLowerCase() !== "null";
	}

	hasEmployeeName(record: TransactionReport): boolean {
		return this.isValidName(record.employeeFirstName) && this.isValidName(record.employeeLastName);
	}
}
